import React, { useState, useEffect } from 'react';
import { Server, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { SERVER_CONFIG, ServerEnvironment, getCurrentEnvironment, setServerEnvironment } from '../../config/server.config';

interface ServerSwitchProps {
  floating?: boolean;
}

export const ServerSwitch: React.FC<ServerSwitchProps> = ({ floating = true }) => {
  const [environment, setEnvironmentState] = useState<ServerEnvironment>('production');
  const [isConnected, setIsConnected] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // Cargar preferencia guardada al montar
  useEffect(() => {
    const savedEnv = getCurrentEnvironment();
    setEnvironmentState(savedEnv);
    // Verificar conexión inicial
    checkConnection(savedEnv);
  }, []);

  const checkConnection = async (env: ServerEnvironment) => {
    if (isChecking) return;
    
    setIsChecking(true);
    const url = SERVER_CONFIG[env].base;
    
    try {
      const response = await axios.get(`${url}/health`, {
        timeout: 5000
      });
      
      if (response.status === 200) {
        setIsConnected(true);
      }
    } catch (error) {
      setIsConnected(false);
      console.error(`No se pudo conectar a ${env}:`, error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleToggle = async () => {
    const newEnv = environment === 'local' ? 'production' : 'local';
    
    // Guardar en localStorage
    setServerEnvironment(newEnv);
    setEnvironmentState(newEnv);
    
    // Verificar conexión con el nuevo servidor
    await checkConnection(newEnv);
    
    // Notificar el cambio
    toast.success(
      `Cambiado a servidor ${newEnv === 'local' ? 'local' : 'de producción'}`
    );
    
    // Recargar la página después de un breve delay para aplicar los cambios
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const containerClass = floating 
    ? "fixed bottom-4 left-4 z-50 flex items-center space-x-3 p-3 bg-white shadow-lg rounded-lg border border-gray-200"
    : "flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200";

  return (
    <div className={containerClass}>
      <div className="flex items-center space-x-2">
        <Server className="h-4 w-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">
          Servidor:
        </span>
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={handleToggle}
          disabled={isChecking}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors
            ${environment === 'production' 
              ? 'bg-green-600' 
              : 'bg-blue-600'
            }
            ${isChecking ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
              ${environment === 'production' ? 'translate-x-6' : 'translate-x-1'}
            `}
          />
        </button>
        
        <div className="flex items-center space-x-2">
          <span className={`text-sm font-semibold ${
            environment === 'local' 
              ? 'text-blue-600' 
              : 'text-green-600'
          }`}>
            {environment === 'local' ? 'Local' : 'Producción'}
          </span>
          
          {isChecking ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
          ) : isConnected ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" />
          )}
        </div>
      </div>
      
      <div className="text-xs text-gray-500 ml-2">
        {SERVER_CONFIG[environment].base.replace('https://', '').replace('http://', '')}
      </div>
    </div>
  );
};